/**
 * The forward view (v8.0.0, Blueprint §9): what is due soon, and how much of it
 * is already set aside. PURE.
 *
 * §9: "surfaced in the Needs You forward view ('$X due in 14/30 days; $Y
 * reserved…')". The shortfall half of that sentence is NOT used: CLAUDE.md's
 * convention is percentage-of-target framing, never shortfall, so this says
 * how much is set aside, as a share.
 *
 * Built from envelopes, so a bill tracked from the bank and a deadline the
 * operator set by hand are the same thing here — one obligation model.
 */

import { daysUntil, duePhrase } from './due-date';
import { fundedFraction, type EnvelopeTileModel } from './envelope-math';

/** How far ahead the forward view looks. §9 names 14 and 30; the focal alert already covers 14. */
export const FORWARD_WINDOW_DAYS = 30;

export interface ForwardItem {
  id: string;
  name: string;
  daysUntilDue: number;
  dueText: string;
  targetMinor: number;
  /** NULL when the balance is unknown. */
  percentSetAside: number | null;
}

export interface ForwardView {
  items: ForwardItem[];
  dueMinor: number;
  /** Σ min(balance, target). NULL if any balance is unknown — never a guess. */
  setAsideMinor: number | null;
  /** Whole percent of dueMinor set aside, or NULL when unknown. */
  percentSetAside: number | null;
}

/**
 * Everything with a target and a date that is overdue or due within the window,
 * soonest first. Null when there is nothing — no empty card.
 */
export function forwardView(
  envelopes: readonly EnvelopeTileModel[],
  now: Date = new Date(),
  windowDays = FORWARD_WINDOW_DAYS,
): ForwardView | null {
  const items: ForwardItem[] = [];
  let dueMinor = 0;
  let setAside: number | null = 0;

  for (const e of envelopes) {
    if (e.type === 'unallocated' || e.targetMinor == null || e.targetMinor <= 0) continue;
    const days = daysUntil(e.targetDate, now);
    if (days == null || days > windowDays) continue;

    const fraction = fundedFraction(e.balanceMinor, e.targetMinor);
    items.push({
      id: e.id,
      name: e.name,
      daysUntilDue: days,
      dueText: duePhrase(days),
      targetMinor: e.targetMinor,
      percentSetAside: fraction == null ? null : Math.round(fraction * 100),
    });
    dueMinor += e.targetMinor;
    setAside =
      setAside == null || e.balanceMinor == null
        ? null
        : setAside + Math.min(Math.max(0, e.balanceMinor), e.targetMinor);
  }

  if (items.length === 0) return null;
  items.sort((a, b) => a.daysUntilDue - b.daysUntilDue || a.name.localeCompare(b.name));
  return {
    items,
    dueMinor,
    setAsideMinor: setAside,
    percentSetAside:
      setAside == null || dueMinor === 0 ? null : Math.floor((setAside / dueMinor) * 100),
  };
}
